import { createAction, createSlice } from '@reduxjs/toolkit';
import { openSuccessNotification } from '@/notification';
import { runUpdateMetadataMutation } from '@/pages/update-metadata/update-metadata.query';
import { notifyUnlessUnauthorized } from '@/pages/shared/rtk-query-helpers';

const SET_VALUE = 'SET_VALUE';
const CHANGE_TABLE_ITEM_VALUE = 'CHANGE_TABLE_ITEM_VALUE';
const CHANGE_TABLE_APPLY_ALL = 'CHANGE_TABLE_APPLY_ALL';
const CHANGE_TABLE_APPLY_SELECTED = 'CHANGE_TABLE_APPLY_SELECTED';
const UPDATE_METADATA_ADD_SELECT_DOCUMENT = 'UPDATE_METADATA_ADD_SELECT_DOCUMENT';
const UPDATE_METADATA_DELETE_SELECT_DOCUMENT = 'UPDATE_METADATA_DELETE_SELECT_DOCUMENT';
const SET_DOCUMENTS_INITIAL_SELECT_DOCUMENT = 'SET_DOCUMENTS_INITIAL_SELECT_DOCUMENT';
const DELETE_DOCUMENTS_FROM_ARRAY = 'DELETE_DOCUMENTS_FROM_ARRAY';
const SET_WORK_ITEMS = 'SET_WORK_ITEMS';
const SET_TO_INITIAL_DOCUMENTS = 'SET_TO_INITIAL_DOCUMENTS';

const initialState = {
    selectedRows: [],
    lastSelectedItem: null,
    workItems: []
};

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const setValue = createAction(SET_VALUE, (key, value) => ({
    payload: { key, value }
}));

export const setTableValue = createAction(CHANGE_TABLE_ITEM_VALUE, (documentId, key, value) => ({
    payload: { documentId, key, value }
}));

export const applyAllTable = createAction(CHANGE_TABLE_APPLY_ALL, (model, preserveExistingValues) => ({
    payload: { model, preserveExistingValues }
}));

export const applySelectedTable = createAction(CHANGE_TABLE_APPLY_SELECTED, (model, preserveExistingValues, items = null) => ({
    payload: { model, preserveExistingValues, items }
}));

export const addSelected = createAction(UPDATE_METADATA_ADD_SELECT_DOCUMENT, (item, isShifted) => ({
    payload: { item, isShifted }
}));

export const deleteSelected = createAction(UPDATE_METADATA_DELETE_SELECT_DOCUMENT, (item, isShifted) => ({
    payload: { item, isShifted }
}));

export const deleteDocs = createAction(DELETE_DOCUMENTS_FROM_ARRAY, (list) => ({
    payload: { list }
}));

export const setWorkItems = createAction(SET_WORK_ITEMS, (items) => ({
    payload: { items }
}));

export const setDocumentsToInitial = createAction(SET_DOCUMENTS_INITIAL_SELECT_DOCUMENT);

export const setDocumentsAsInitial = createAction(SET_TO_INITIAL_DOCUMENTS);

const applyFieldsToDocument = (doc, model, preserveExistingValues) => {
    for (const key in model) {
        const fieldIsEmpty = doc[key] === undefined || doc[key] === null || doc[key] === '';

        if (!preserveExistingValues || fieldIsEmpty) {
            doc[key] = model[key];
        }
    }
};

const updateMetadataSlice = createSlice({
    name: 'updateMetadata',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setValue, (draft, action) => {
                draft[action.payload.key] = action.payload.value;
            })
            .addCase(setTableValue, (draft, action) => {
                draft.documents.find(x => x.documentId == action.payload.documentId)[action.payload.key] = action.payload.value;
            })
            .addCase(setWorkItems, (draft, action) => {
                draft.workItems = JSON.parse(JSON.stringify(action.payload.items));
            })
            .addCase(applyAllTable, (draft, action) => {
                draft.documents.forEach(element => {
                    applyFieldsToDocument(element, action.payload.model, action.payload.preserveExistingValues);
                });
            })
            .addCase(applySelectedTable, (draft, action) => {
                const docIds = action.payload.items ? action.payload.items : draft.selectedRows.map(x => x.documentId);

                docIds.forEach(element => {
                    const item = draft.documents.find(x => x.documentId == element);
                    if (!item) return; // Skip if item not found

                    applyFieldsToDocument(item, action.payload.model, action.payload.preserveExistingValues);
                });
            })
            .addCase(addSelected, (draft, action) => {
                let updatedSelectedRows = [...draft.selectedRows];

                if (!updatedSelectedRows.find(item => item.documentId == action.payload.item.documentId)) {
                    updatedSelectedRows = [...updatedSelectedRows, action.payload.item];
                }

                if (action.payload.isShifted && draft.lastSelectedItem != null) {
                    let index1 = draft.documents.findIndex(item => item.documentId == action.payload.item.documentId);
                    let index2 = draft.documents.findIndex(item => item.documentId == draft.lastSelectedItem.documentId);

                    if (index2 < index1) {
                        const tmp = index1;
                        index1 = index2;
                        index2 = tmp;
                    }

                    for (let i = index1; i <= index2; i++) {
                        const doc = draft.documents[i];
                        if (!updatedSelectedRows.find(item => item.documentId == doc.documentId)) {
                            updatedSelectedRows = [...updatedSelectedRows, doc];
                        }
                    }
                }

                draft.selectedRows = updatedSelectedRows;
                draft.lastSelectedItem = action.payload.item;
            })
            .addCase(deleteSelected, (draft, action) => {
                const arrayForDelete = [action.payload.item];

                if (action.payload.isShifted && draft.lastSelectedItem != null) {
                    let index1 = draft.documents.findIndex(item => item.documentId == action.payload.item.documentId);
                    let index2 = draft.documents.findIndex(item => item.documentId == draft.lastSelectedItem.documentId);

                    if (index2 < index1) {
                        const tmp = index1;
                        index1 = index2;
                        index2 = tmp;
                    }

                    for (let i = index1; i < index2; i++) {
                        arrayForDelete.push(draft.documents[i]);
                    }
                }

                draft.selectedRows = draft.selectedRows.filter(
                    row => !arrayForDelete.some(item => item.documentId === row.documentId)
                );
                draft.lastSelectedItem = action.payload.item;
            })
            .addCase(setDocumentsToInitial, (draft) => {
                draft.documents = JSON.parse(JSON.stringify(draft.initialDocuments.slice()));
            })
            .addCase(setDocumentsAsInitial, (draft) => {
                draft.initialDocuments = JSON.parse(JSON.stringify(draft.documents.slice()));
            })
            .addCase(deleteDocs, (draft, action) => {
                draft.documents = draft.initialDocuments.filter(x => !action.payload.list.find(z => z.documentId == x.documentId));
            });
    }
});

const updateMetadataReducer = updateMetadataSlice.reducer;


export const uploadTable = documentIds => {
    return async (dispatch) => {
        dispatch(setValue('tableLoading', true));

        try {
            const documents = await runUpdateMetadataMutation(dispatch, 'getDocumentsDetails', documentIds);

            dispatch(setValue('documents', [...documents.slice()]));
            dispatch(setValue('initialDocuments', JSON.parse(JSON.stringify(documents.slice()))));
        } catch (error) {
            notifyUnlessUnauthorized(error, "Error. Can't load documents");
        } finally {
            setTimeout(() => {
                dispatch(setValue('tableLoading', false));
            }, 0)
        }
    }
}

export const updateDocuments = (model, needClose) => {
    return async (dispatch) => {
        dispatch(setValue('tableLoading', true));

        try {
            await runUpdateMetadataMutation(dispatch, 'updateDocumentsDetailsList', model);

            dispatch(setDocumentsAsInitial());
            openSuccessNotification('Documents are updated');

            setTimeout(() => {
                if (needClose) {
                    window.location.assign("/");
                }
            }, 0)
        } catch (error) {
            notifyUnlessUnauthorized(error, "Error. Can't update documents");
        } finally {
            setTimeout(() => {
                dispatch(setValue('tableLoading', false));
            }, 0)
        }
    }
}

export const clearSelected = () => {
    return (dispatch) => {
        dispatch(setValue('selectedRows', []));
        dispatch(setValue('lastSelectedItem', null));

    }
}


export default updateMetadataReducer;